import { Link } from 'react-router-dom'
import { CreditCard, TrendingUp, AlertTriangle, Plus } from 'lucide-react'
import { Card, Button } from '../UI'

const CreditBalance = ({ credits, loading, onBuyCredits }) => {
  const balance = credits?.balance || 0
  const allottedMonthly = credits?.allottedMonthly || 0
  const usedThisMonth = credits?.usedThisMonth || 0
  const purchased = credits?.purchased || 0
  
  // Calculate usage percentage
  const usagePercentage = allottedMonthly > 0 
    ? Math.min(100, Math.round((usedThisMonth / allottedMonthly) * 100))
    : 0
  
  // Check if low on credits
  const isLowOnCredits = balance > 0 && allottedMonthly > 0 && balance < (allottedMonthly * 0.2)

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  if (loading) {
    return (
      <Card padding="lg">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-5 bg-dark-700 rounded w-28" />
            <div className="h-8 w-8 bg-dark-700 rounded-lg" />
          </div>
          <div className="h-12 bg-dark-700 rounded w-32 mb-2" />
          <div className="h-4 bg-dark-700 rounded w-40 mb-4" />
          <div className="h-2 bg-dark-700 rounded w-full mb-4" />
          <div className="h-10 bg-dark-700 rounded w-full" />
        </div>
      </Card>
    )
  }

  return (
    <Card padding="lg" className={isLowOnCredits ? 'border-yellow-500/30' : ''}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-dark-400 text-sm font-medium">Credit Balance</span>
        <div className={`p-2 rounded-lg ${isLowOnCredits ? 'bg-yellow-500/10' : 'bg-accent-500/10'}`}>
          <CreditCard className={`w-5 h-5 ${isLowOnCredits ? 'text-yellow-400' : 'text-accent-400'}`} />
        </div>
      </div>

      {/* Main Balance */}
      <div className="mb-4">
        <p className="text-4xl font-bold mb-1">
          {formatNumber(balance)}
        </p>
        <p className="text-dark-400 text-sm">
          credits available
        </p>
      </div>

      {/* Low Credit Warning */}
      {isLowOnCredits && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/10 rounded-lg mb-4">
          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <span className="text-sm text-yellow-300">
            Running low on credits
          </span>
        </div>
      )}

      {/* Usage Progress Bar */}
      {allottedMonthly > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-dark-400">Monthly usage</span>
            <span className="text-dark-300">{usagePercentage}%</span>
          </div>
          <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                usagePercentage > 80 
                  ? 'bg-gradient-to-r from-yellow-500 to-red-500' 
                  : 'bg-gradient-to-r from-accent-500 to-accent-400'
              }`}
              style={{ width: `${usagePercentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-dark-500 mt-1">
            <span>{formatNumber(usedThisMonth)} used</span>
            <span>{formatNumber(allottedMonthly)} allotted</span>
          </div>
        </div>
      )}

      {/* Breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-dark-800/50 rounded-lg">
        <div>
          <p className="text-dark-500 text-xs mb-1">Monthly</p>
          <p className="font-medium">{formatNumber(allottedMonthly)}</p>
        </div>
        <div>
          <p className="text-dark-500 text-xs mb-1">Purchased</p>
          <p className="font-medium">{formatNumber(purchased)}</p>
        </div>
      </div>

      {/* Buy Credits Button */}
      {onBuyCredits ? (
        <Button variant="outline" fullWidth onClick={onBuyCredits}>
          <Plus className="w-4 h-4" />
          Buy Credits
        </Button>
      ) : (
        <Link to="/pricing#credits">
          <Button variant="outline" fullWidth>
            <Plus className="w-4 h-4" />
            Buy Credits
          </Button>
        </Link>
      )}
    </Card>
  )
}

export default CreditBalance
